import React from "react"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

import { useAuth } from "hooks/useAuth"
import FormElement from "components/Form/FormElement"

import { MC_OPERATOR, BC_OPERATOR, MFC_OPERATOR } from "utils/constants"
import { CheckOutlineIcon } from "components/Elements/Icons/CheckOutline"
import { isNotEmpty } from "utils"
import { LocalEntity } from "models"
import { FormError } from "models/form"

const defaultFormFields = {
	isBicycode: false,
	isMFC: false,
	isMoustache: false,
	is_activate_by_G8: false,
}

type OperatorsFormProps = {
	partner: LocalEntity
	setUpdatedPartner: React.Dispatch<React.SetStateAction<LocalEntity>>
	setErrors: React.Dispatch<React.SetStateAction<Array<FormError>>>
	isEdit: boolean
	showLocalErrors: boolean
}

export const OperatorsForm = ({
	partner,
	setUpdatedPartner,
	setErrors,
	isEdit,
	showLocalErrors,
}: OperatorsFormProps) => {
	const [formFields, setFormFields] = React.useState<Partial<LocalEntity>>(defaultFormFields)

	const { t } = useTranslation()
	const { isAdminOCode, isAdminBicycode, isAdminMFC, isAdminMoustache } = useAuth()

	React.useEffect(
		() => {
			// When partner reloaded (after await fetchData()), set data in formFields
			if (partner || !isEdit)
				setFormFields((s) => ({
					...s,
					isBicycode: partner?.isBicycode,
					isMFC: partner?.isMFC,
					isMoustache: partner?.isMoustache,
					is_activate_by_G8: partner?.is_activate_by_G8,
				}))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[partner, isEdit]
	)

	React.useEffect(
		() => {
			// When we create or update a partner, we have to pass data to parent directly.
			setUpdatedPartner((prevFormFields) => ({
				...prevFormFields,
				isBicycode: formFields.isBicycode,
				isMFC: formFields.isMFC,
				isMoustache: formFields.isMoustache,
			}))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formFields]
	)

	const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name
		setFormFields((s) => ({
			...s,
			[name]: value,
		}))
	}, [])

	const handleValidation = React.useCallback((name: string, errs: Array<FormError>) => {
		setErrors((s: Array<FormError>) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
	}

	if (!isNotEmpty(formFields)) return <></>

	return (
		<section id="operator" className="py-16 px-4 scroll-mt-10">
			<h2 className="c-h2 font-semibold mb-2 uppercase">
				{t("edit.operator_for", { ns: "partner" })}
			</h2>

			{!isAdminOCode && (
				<p className="font-bold mt-4">
					{t("edit.authorize-registration", { ns: "partner" })}
				</p>
			)}

			<div className="l-grid">
				{(isAdminOCode || isAdminBicycode) && (
					<div
						className={clsx("", {
							"pt-5": !isAdminOCode,
							"p-5": isAdminOCode,
						})}
					>
						<FormElement
							{...formElementCommonProps}
							value={formFields.isBicycode}
							name="isBicycode"
							type="checkbox"
							image={BC_OPERATOR}
							className={clsx("", {
								"py-4": isAdminOCode,
							})}
							classNameInput={clsx("", {
								"m-0": !isAdminOCode,
							})}
							classNameOption={clsx("items-center", {
								"flex-row": !isAdminOCode,
								"flex-col": isAdminOCode,
							})}
							disabled={!isEdit}
						/>
						{formFields?.isBicycode && formFields.is_activate_by_G8 && (
							<p className="flex justify-center mt-5">
								<span className="mr-1">
									{t("futurosoft_activate", { ns: "partner" })}
								</span>
								<CheckOutlineIcon size={24} />
							</p>
						)}
					</div>
				)}

				{(isAdminOCode || isAdminMFC) && (
					<div
						className={clsx("", {
							"pt-5": !isAdminOCode,
							"p-5": isAdminOCode,
						})}
					>
						<FormElement
							{...formElementCommonProps}
							value={formFields.isMFC}
							name="isMFC"
							type="checkbox"
							image={MFC_OPERATOR}
							className={clsx("", {
								"py-4": isAdminOCode,
							})}
							classNameOption={clsx("items-center", {
								"flex-row": !isAdminOCode,
								"flex-col": isAdminOCode,
							})}
							disabled={!isEdit}
						/>
					</div>
				)}

				{(isAdminOCode || isAdminMoustache) && (
					<div
						className={clsx("", {
							"pt-5": !isAdminOCode,
							"p-5": isAdminOCode,
						})}
					>
						<FormElement
							{...formElementCommonProps}
							value={formFields.isMoustache}
							name="isMoustache"
							type="checkbox"
							image={MC_OPERATOR}
							className={clsx("", {
								"py-4": isAdminOCode,
							})}
							classNameOption={clsx("items-center", {
								"flex-row": !isAdminOCode,
								"flex-col": isAdminOCode,
							})}
							disabled={!isEdit}
						/>
					</div>
				)}
			</div>
		</section>
	)
}
